import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ComentariosDoVeiculo as Dados,
  ComentarioEscopo,
  ThreadComentario,
  criarComentario,
  obterComentariosDoVeiculo,
  responderComentario,
} from '../../api/comentariosApi';
import { notify } from '../../shared/feedback/notify';
import CampoComMencoes, { MencaoEscolhida, idsMencionadosNoTexto } from './CampoComMencoes';
import TextoComMencoes from './TextoComMencoes';
import './ComentariosDoVeiculo.css';

interface ComentariosDoVeiculoProps {
  carId: string;
  /**
   * Restringe a exibição à thread de UMA sub-tarefa, e faz o campo novo nascer nesse escopo.
   * É como a linha da sub-tarefa abre a conversa dela (at-90).
   */
  apenasSubtarefaId?: string;
  /** Esconde a autoria (tela do setor de Eventos acompanha o serviço, não as pessoas). */
  ocultarAutoria?: boolean;
  /** Notifica o hospedeiro de que a contagem mudou (usado pelo indicador da linha da sub-tarefa). */
  onTotalChange?: (total: number) => void;
}

const formatarDataHora = (valor: string): string | null => {
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Seção de comentários do veículo (at-90, ADR 0042).
 *
 * Compartilhada pelos modais de placa de todos os papéis e pela aba Feedback da Auditoria, no
 * mesmo molde do `ProgressoSubtarefas` (at-89) e do `ObservacoesAtendimento` (at-54).
 *
 * 🔴 Quatro regras que não podem ser afrouxadas:
 * 1. **Falha de carga NUNCA vira "ninguém comentou"** (at-60). Aqui o falso vazio é caro: o
 *    comentário é o que a Recepção lê para responder ao associado.
 * 2. **O campo de escrita depende das permissões EFETIVAS** que vêm na própria resposta — nunca
 *    do papel do usuário no frontend. A matriz é configurável, e duplicar a regra aqui faria as
 *    duas divergirem no primeiro ajuste.
 * 3. **Comentário é IMUTÁVEL**: não há botão de editar nem de excluir, em nenhum lugar.
 * 4. **Comentário de outro atendimento é MARCADO**: sem a marca, um comentário de seis meses
 *    atrás parece ser desta entrada do veículo.
 */
const ComentariosDoVeiculo: React.FC<ComentariosDoVeiculoProps> = ({
  carId,
  apenasSubtarefaId,
  ocultarAutoria,
  onTotalChange,
}) => {
  const [dados, setDados] = useState<Dados | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Rascunho do comentário novo. O escopo nasce em PLACA (o mais abrangente) fora da sub-tarefa.
  const [texto, setTexto] = useState('');
  const [mencoes, setMencoes] = useState<MencaoEscolhida[]>([]);
  const [escopoEscolhido, setEscopoEscolhido] = useState<string>('PLACA');

  // Resposta em edição: id da raiz + texto. `null` = nenhuma aberta.
  const [respondendoId, setRespondendoId] = useState<string | null>(null);
  const [textoResposta, setTextoResposta] = useState('');
  const [mencoesResposta, setMencoesResposta] = useState<MencaoEscolhida[]>([]);

  const carregar = useCallback(async () => {
    if (!carId) return;
    setCarregando(true);
    try {
      const resposta = await obterComentariosDoVeiculo(carId);
      setDados(resposta);
      setFalhou(false);
    } catch (error) {
      /*
       * Estado seguro é `null` + sinal explícito. Deixar a lista vazia aqui produziria exatamente
       * a leitura errada que a regra da at-60 existe para evitar — e que nesta tela chega ao
       * associado como "ninguém registrou nada sobre o seu carro".
       */
      setDados(null);
      setFalhou(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar os comentários deste veículo. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, [carId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const threads = useMemo<ThreadComentario[]>(() => {
    const todas = Array.isArray(dados?.threads) ? dados!.threads : [];
    // Modo sub-tarefa: só a conversa daquela parte do serviço.
    return apenasSubtarefaId
      ? todas.filter((t) => t.atividadeSubtarefaId === apenasSubtarefaId)
      : todas;
  }, [dados, apenasSubtarefaId]);

  useEffect(() => {
    onTotalChange?.(threads.length);
  }, [threads.length, onTotalChange]);

  const enviarComentario = async () => {
    const limpo = texto.trim();
    if (!limpo || salvando) return;

    setSalvando(true);
    try {
      const escopo: ComentarioEscopo = apenasSubtarefaId
        ? 'SUBTAREFA'
        : escopoEscolhido === 'PLACA'
          ? 'PLACA'
          : 'ESTEIRA';

      await criarComentario(carId, {
        escopo,
        texto: limpo,
        // at-129: só quem ainda está mencionado no texto — apagou o @, a pessoa não é avisada.
        mencionados: idsMencionadosNoTexto(limpo, mencoes),
        ...(escopo === 'ESTEIRA' ? { esteiraId: escopoEscolhido } : {}),
        ...(escopo === 'SUBTAREFA' ? { atividadeSubtarefaId: apenasSubtarefaId } : {}),
      });

      // Só limpa o rascunho DEPOIS de o backend confirmar: falha com campo limpo perderia o texto.
      setTexto('');
      setMencoes([]);
      notify.success('Comentário registrado.');
      await carregar();
    } catch (error) {
      /*
       * `errorFrom`, não `actionError` (regra da at-65): aqui o MOTIVO do backend é o que
       * orienta — "esta esteira não faz parte do atendimento", "perfil sem permissão de
       * comentar". E `actionError` conjuga o verbo no masculino singular a partir de uma lista
       * fechada de ações (BUG-V51), que não inclui "registrar".
       *
       * O que não pode acontecer é falso sucesso: quem escreveu precisa saber que não gravou.
       */
      notify.errorFrom(
        error,
        'Não foi possível registrar o comentário. Tente novamente; se persistir, acione a TI.',
      );
    } finally {
      setSalvando(false);
    }
  };

  const enviarResposta = async (comentarioId: string) => {
    const limpo = textoResposta.trim();
    if (!limpo || salvando) return;

    setSalvando(true);
    try {
      await responderComentario(comentarioId, limpo, idsMencionadosNoTexto(limpo, mencoesResposta));
      setTextoResposta('');
      setMencoesResposta([]);
      setRespondendoId(null);
      notify.success('Resposta registrada.');
      await carregar();
    } catch (error) {
      // "resposta" é feminino: `actionError` produziria "Resposta registrado" (BUG-V51).
      notify.errorFrom(
        error,
        'Não foi possível registrar a resposta. Tente novamente; se persistir, acione a TI.',
      );
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return (
      <div className="comentarios-veiculo">
        <span className="comentarios-veiculo-carregando">Carregando comentários...</span>
      </div>
    );
  }

  if (falhou) {
    return (
      <div className="comentarios-veiculo">
        <p className="comentarios-veiculo-erro" role="alert">
          Não foi possível verificar os comentários deste veículo. Pode haver comentários que não
          estão sendo exibidos — recarregue a página antes de informar o associado.
        </p>
      </div>
    );
  }

  const podeComentar = Boolean(dados?.podeComentar);
  const podeResponder = Boolean(dados?.podeResponder);
  const esteiras = Array.isArray(dados?.esteirasComentaveis) ? dados!.esteirasComentaveis : [];

  /*
   * Sem comentários E sem permissão de escrever: nada a mostrar. É o mesmo critério do
   * `ProgressoSubtarefas` — bloco vazio em modal de consulta só gasta espaço. Quem PODE escrever
   * continua vendo a seção, senão não teria por onde começar a conversa.
   */
  if (threads.length === 0 && !podeComentar) return null;

  return (
    <div className="comentarios-veiculo">
      <div className="comentarios-veiculo-cabecalho">
        <span className="comentarios-veiculo-titulo">
          {apenasSubtarefaId ? 'Comentários desta sub-tarefa' : 'Comentários'}
        </span>
        {threads.length > 0 && (
          <span className="comentarios-veiculo-contador">{threads.length}</span>
        )}
      </div>

      {!apenasSubtarefaId && (
        <p className="comentarios-veiculo-hint">
          Anotações internas da equipe sobre o que está acontecendo com o veículo.{' '}
          <strong>O associado não tem acesso a este conteúdo</strong>, e o que for registrado aqui
          não pode ser editado nem apagado. Digite <strong>@</strong> para mencionar alguém: só
          quem for escolhido na lista é avisado, e a menção substitui o aviso comum que essa pessoa
          receberia.
        </p>
      )}

      {threads.length === 0 ? (
        <p className="comentarios-veiculo-vazio">
          Nenhum comentário registrado {apenasSubtarefaId ? 'nesta sub-tarefa' : 'neste veículo'}.
        </p>
      ) : (
        <ul className="comentarios-veiculo-lista">
          {threads.map((thread) => {
            const quando = formatarDataHora(thread.criadoEm);

            return (
              <li key={thread.id} className="comentarios-veiculo-thread">
                <div className="comentarios-veiculo-thread-topo">
                  <span className="comentarios-veiculo-escopo" title={thread.escopoLabel}>
                    {thread.escopoLabel}
                  </span>
                  {/* at-90: comentário de placa nascido em outro atendimento. Sem a marca, ele
                      parece ser desta entrada do veículo. */}
                  {thread.deOutroAtendimento && (
                    <span
                      className="comentarios-veiculo-badge-outro"
                      title="Registrado em uma passagem anterior deste veículo pela oficina."
                    >
                      Atendimento anterior
                    </span>
                  )}
                  <span className="comentarios-veiculo-assinatura">
                    {ocultarAutoria ? quando : [thread.autorNome, quando].filter(Boolean).join(' - ')}
                  </span>
                </div>

                <p className="comentarios-veiculo-texto">
                  <TextoComMencoes texto={thread.texto} mencionados={thread.mencionados} />
                </p>

                {thread.respostas.length > 0 && (
                  <ul className="comentarios-veiculo-respostas">
                    {thread.respostas.map((resposta) => {
                      const quandoResposta = formatarDataHora(resposta.criadoEm);
                      return (
                        <li key={resposta.id} className="comentarios-veiculo-resposta">
                          <span className="comentarios-veiculo-assinatura">
                            {ocultarAutoria
                              ? quandoResposta
                              : [resposta.autorNome, quandoResposta].filter(Boolean).join(' - ')}
                          </span>
                          <p className="comentarios-veiculo-texto">
                            <TextoComMencoes texto={resposta.texto} mencionados={resposta.mencionados} />
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {podeResponder && respondendoId !== thread.id && (
                  <button
                    type="button"
                    className="comentarios-veiculo-link"
                    onClick={() => {
                      setRespondendoId(thread.id);
                      setTextoResposta('');
                      setMencoesResposta([]);
                    }}
                  >
                    Responder
                  </button>
                )}

                {podeResponder && respondendoId === thread.id && (
                  <div className="comentarios-veiculo-form-resposta">
                    <CampoComMencoes
                      placeholder="Escreva a resposta (use @ para mencionar alguém)"
                      value={textoResposta}
                      onChange={setTextoResposta}
                      mencoes={mencoesResposta}
                      onMencoesChange={setMencoesResposta}
                      ariaLabel="Resposta ao comentário"
                    />
                    <div className="comentarios-veiculo-acoes">
                      <button
                        type="button"
                        className="comentarios-veiculo-btn"
                        disabled={!textoResposta.trim() || salvando}
                        onClick={() => void enviarResposta(thread.id)}
                      >
                        {salvando ? 'Registrando...' : 'Registrar resposta'}
                      </button>
                      <button
                        type="button"
                        className="comentarios-veiculo-btn-secundario"
                        onClick={() => {
                          setRespondendoId(null);
                          setTextoResposta('');
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {podeComentar && (
        <div className="comentarios-veiculo-form">
          {/* Seletor de escopo (decisão do Victor): "Geral da placa" ou uma esteira do
              atendimento — inclusive etapa já encerrada, que é o caso do desamassado refeito.
              No modo sub-tarefa o escopo já está definido pela linha que abriu a conversa. */}
          {!apenasSubtarefaId && esteiras.length > 0 && (
            <label className="comentarios-veiculo-label">
              Sobre o que é este comentário
              <select
                className="comentarios-veiculo-select"
                value={escopoEscolhido}
                onChange={(e) => setEscopoEscolhido(e.target.value)}
              >
                <option value="PLACA">Geral da placa (vale para o veículo)</option>
                {esteiras.map((esteira) => (
                  <option key={esteira.id} value={esteira.id}>
                    Etapa: {esteira.nome}
                  </option>
                ))}
              </select>
            </label>
          )}

          <CampoComMencoes
            placeholder="O que está acontecendo com este veículo? Use @ para mencionar alguém."
            value={texto}
            onChange={setTexto}
            mencoes={mencoes}
            onMencoesChange={setMencoes}
            ariaLabel="Novo comentário"
          />
          <div className="comentarios-veiculo-acoes">
            <button
              type="button"
              className="comentarios-veiculo-btn"
              disabled={!texto.trim() || salvando}
              onClick={() => void enviarComentario()}
            >
              {salvando ? 'Registrando...' : 'Registrar comentário'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComentariosDoVeiculo;
